import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const WHATSAPP_NUMBER = "963992367582";
const WHATSAPP_MESSAGE = "مرحباً، أريد الاستفسار عن خدماتكم";

const PublicNavbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;


    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinks = [
        { name: "الرئيسية", path: "/" },
        { name: "مشاريعنا", path: "/portfolio" },
        { name: "المدونة", path: "/blog" },
        { name: "الفيديوهات", path: "/videos" },
        { name: "من نحن", path: "/about" },
        { name: "تواصل معنا", path: "/contact" },
    ];

    const handleLinkClick = () => {
        setIsMobileMenuOpen(false);
    };

    return (
        <nav
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
                isScrolled
                    ? "bg-background/40 backdrop-blur-xl border-b border-white/5 py-4 shadow-2xl"
                    : "bg-transparent py-8"
            )}
            dir="rtl"
        >
            <div className="container mx-auto px-6 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2 group">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                        <span className="text-background font-black text-xl">T</span>
                    </div>
                    <span className="text-2xl font-bold tracking-tighter text-white">
                        TRBD
                    </span>
                </Link>

                {/* Desktop Links */}
                <div className="hidden md:flex items-center gap-10">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={cn(
                                "text-sm font-medium transition-all duration-300 relative py-1",
                                "hover:text-white",
                                location.pathname === link.path
                                    ? "text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-full"
                                    : "text-muted-foreground"
                            )}
                        >
                            {link.name}
                        </Link>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="hidden md:flex items-center gap-6">
                    {isAuthenticated ? (
                        <Link to="/dashboard">
                            <span className="text-sm font-medium text-muted-foreground hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                                <LayoutDashboard size={16} />
                                لوحة التحكم
                            </span>
                        </Link>
                    ) : (
                        <Link to="/login">
                            <span className="text-sm font-medium text-muted-foreground hover:text-white transition-colors cursor-pointer">
                                تسجيل الدخول
                            </span>
                        </Link>
                    )}
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-[#128C7E] text-white hover:bg-[#075E54] font-bold px-6 rounded-full transition-all duration-300 shadow-tech gap-2">
                            <MessageCircle size={18} />
                            تواصل واتساب
                        </Button>
                    </a>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="md:hidden w-10 h-10 flex items-center justify-center text-white bg-white/5 rounded-lg border border-white/10"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label={isMobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                >
                    {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div
                    id="mobile-menu"
                    role="navigation"
                    aria-label="القائمة الرئيسية"
                    className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-2xl border-b border-white/5 p-6 flex flex-col gap-6 animate-in fade-in slide-in-from-top-4 duration-300"
                >
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={cn(
                                "text-lg font-medium transition-colors",
                                location.pathname === link.path
                                    ? "text-primary"
                                    : "text-foreground/80"
                            )}
                            onClick={handleLinkClick}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <div className="flex flex-col gap-4 pt-6 border-t border-white/5">
                        {isAuthenticated ? (
                            <Link to="/dashboard" onClick={handleLinkClick}>
                                <Button variant="ghost" className="w-full justify-start text-muted-foreground gap-2">
                                    <LayoutDashboard size={18} />
                                    لوحة التحكم
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/login" onClick={handleLinkClick}>
                                <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                                    تسجيل الدخول
                                </Button>
                            </Link>
                        )}
                        <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={handleLinkClick}>
                            <Button className="w-full bg-[#128C7E] text-white font-bold h-12 gap-2">
                                <MessageCircle size={18} />
                                تواصل واتساب
                            </Button>
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default PublicNavbar;
